import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'

/**
 * Per-provider drill-down: master credentials editor, per-course default-rate
 * management, and a list of resellers consuming this integration with their
 * course assignments.
 *
 * Three sections, vertically stacked:
 *   1. Master credentials (form, masked secrets, save button)
 *   2. Courses available through this provider — local + (for TTC) catalog
 *      virtual rows. Each row has an Edit/Import-rate action that opens
 *      the existing rate-picker modal.
 *   3. Resellers using the integration — grouped, with per-reseller course list.
 */
export default function CourseIntegrationDetail() {
  const { provider } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // course row being rate-edited

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get(`/admin/course-integrations/providers/${provider}`)
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [provider])

  if (loading) {
    return (
      <AdminShell title="Course Integration">
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      </AdminShell>
    )
  }
  if (error || !data) {
    return (
      <AdminShell title="Course Integration">
        <div style={errorBoxStyle}>{error || 'Provider not found'}</div>
        <Link to="/admin/course-integrations" style={linkStyle}>← Back to integrations</Link>
      </AdminShell>
    )
  }

  return (
    <AdminShell title={data.provider.label}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/admin/course-integrations" style={linkStyle}>← All integrations</Link>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <StatusBadge status={data.provider.status} />
        <span style={{ color: '#64748b', fontSize: 14 }}>{data.provider.description}</span>
      </div>

      <CredentialsSection
        provider={data.provider}
        credentials={data.credentials}
        onSaved={reload}
      />

      <CoursesSection
        providerKey={data.provider.key}
        courses={data.courses}
        onEdit={setEditing}
      />

      <ResellersSection resellers={data.resellers} />

      {editing && (
        <RatePickerModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
    </AdminShell>
  )
}

// ─── Section: Credentials editor ────────────────────────────────────────
function CredentialsSection({ provider, credentials, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(credentials || {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saveNote, setSaveNote] = useState(null)

  // When the parent reloads, sync state.
  useEffect(() => { setValues(credentials || {}); setEditing(false) }, [credentials])

  if (provider.credentialModel === 'platform_owned') {
    return (
      <Section title="Credentials">
        <div style={{ color: '#64748b', fontSize: 14 }}>
          {provider.docNote || 'No external credentials required.'}
        </div>
      </Section>
    )
  }
  if (provider.credentialModel === 'per_course') {
    return (
      <Section title="Credentials">
        <div style={infoBoxStyle}>
          {provider.docNote || 'Credentials live per course.'}
        </div>
      </Section>
    )
  }

  async function save(e) {
    e?.preventDefault?.()
    setSaving(true); setError(null); setSaveNote(null)
    try {
      const { data } = await tournamentApi.put(
        `/admin/course-integrations/providers/${provider.key}/credentials`,
        values
      )
      if (data.note) setSaveNote(data.note)
      setEditing(false)
      onSaved()
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally { setSaving(false) }
  }

  return (
    <Section title="Master credentials" right={
      !editing ? (
        <button onClick={() => setEditing(true)} style={smallBtn}>Edit</button>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setValues(credentials || {}); setEditing(false); setError(null) }} style={smallBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={primaryBtnSmall}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )
    }>
      {provider.docNote && (
        <div style={{ ...infoBoxStyle, marginBottom: 16 }}>{provider.docNote}</div>
      )}
      {error && <div style={errorBoxStyle}>{error}</div>}
      {saveNote && <div style={{ ...infoBoxStyle, background: '#fef3c7', color: '#92400e' }}>{saveNote}</div>}

      <form onSubmit={save}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {provider.credentialFields.map(f => (
              <tr key={f.key}>
                <td style={{ padding: '8px 16px 8px 0', verticalAlign: 'top', color: '#64748b', fontSize: 13, width: 200 }}>
                  {f.label}
                </td>
                <td style={{ padding: '8px 0' }}>
                  {editing ? (
                    f.type === 'select' ? (
                      <select
                        value={values[f.key] || ''}
                        onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="">— choose —</option>
                        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={f.type === 'password' ? 'password' : 'text'}
                        value={values[f.key] || ''}
                        onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                        placeholder={f.type === 'password' ? 'Leave unchanged to keep current' : ''}
                        style={{ ...inputStyle, width: '100%', maxWidth: 480 }}
                      />
                    )
                  ) : (
                    <code style={{ fontSize: 13, color: values[f.key] ? '#0f172a' : '#cbd5e1' }}>
                      {values[f.key] || '(not set)'}
                    </code>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </form>
    </Section>
  )
}

// ─── Section: Courses available ─────────────────────────────────────────
function CoursesSection({ providerKey, courses, onEdit }) {
  const [search, setSearch] = useState('')
  const filtered = useMemo(() => {
    if (!search) return courses
    const s = search.toLowerCase()
    return courses.filter(c =>
      (c.courseName || '').toLowerCase().includes(s) ||
      (c.city || '').toLowerCase().includes(s)
    )
  }, [courses, search])

  const local = filtered.filter(c => !c.isVirtual).length
  const virtual = filtered.filter(c => c.isVirtual).length

  return (
    <Section
      title="Courses available"
      right={
        <input
          placeholder="Search courses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, fontSize: 13, padding: '6px 10px', width: 220 }}
        />
      }
    >
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
        <b>{local}</b> imported · <b>{virtual}</b> in catalog (not yet imported)
      </div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <Th>Course</Th>
              <Th>Location</Th>
              <Th>Default rate</Th>
              <Th style={{ textAlign: 'center' }}>Resellers</Th>
              <Th style={{ width: 1 }}></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr
                key={c.isVirtual ? `ttc-${c.ttcCourseCode}` : `course-${c.courseId}`}
                style={{
                  borderTop: '1px solid #f1f5f9',
                  background: c.isVirtual ? '#fafbff' : 'transparent',
                }}
              >
                <Td>
                  <div style={{ fontWeight: 500, color: '#0f172a' }}>{c.courseName}</div>
                  {c.isVirtual ? (
                    <div style={{ fontSize: 11, color: '#7c3aed' }}>📦 Catalog · {c.ttcCourseCode}</div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      #{c.courseId}
                      {c.isPlatformManaged && <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 6 }}>🔒 InteGolf</span>}
                    </div>
                  )}
                </Td>
                <Td>{c.city || <span style={{ color: '#cbd5e1' }}>—</span>}</Td>
                <Td>
                  {!c.integration.hasRate ? (
                    c.integration.suggestion?.rateName ? (
                      <span style={{ fontSize: 13, color: '#64748b' }}>
                        <span style={{ color: '#f59e0b' }}>★ </span>
                        Suggested: {c.integration.suggestion.rateName}
                      </span>
                    ) : (
                      <span style={pill('#fee2e2', '#991b1b')}>missing</span>
                    )
                  ) : (
                    <span>
                      {c.integration.currentLooksPublic ? <span style={{ color: '#f59e0b' }}>★ </span> : null}
                      <code style={{ fontSize: 12, color: '#0f172a' }}>{c.integration.rateName || c.integration.rateCode}</code>
                      {c.integration.suggestion?.confidence === 'high'
                        && c.integration.suggestion.rateCode
                        && c.integration.suggestion.rateCode !== c.integration.rateCode && (
                        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                          Suggestion differs: {c.integration.suggestion.rateName}
                        </div>
                      )}
                    </span>
                  )}
                </Td>
                <Td style={{ textAlign: 'center', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                  {c.attachedResellers}
                </Td>
                <Td>
                  <button onClick={() => onEdit(c)} style={smallBtn}>
                    {c.isVirtual ? 'Import & set rate' : 'Edit rate'}
                  </button>
                </Td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No courses match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  )
}

// ─── Section: Resellers using ───────────────────────────────────────────
function ResellersSection({ resellers }) {
  return (
    <Section title={`Resellers using this integration (${resellers.length})`}>
      {resellers.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 14, padding: '12px 0' }}>
          No resellers have attached a course through this integration yet.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <Th>Reseller</Th>
                <Th>Courses</Th>
                <Th>Who pays</Th>
              </tr>
            </thead>
            <tbody>
              {resellers.map(r => (
                <tr key={r.resellerAccountId} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <Td>
                    <Link to={`/admin/reseller-accounts`} style={{ ...linkStyle, fontWeight: 500 }}>
                      {r.resellerName}
                    </Link>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.resellerSlug}</div>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {r.courses.map(c => (
                        <div key={c.courseId} style={{ fontSize: 13 }}>
                          {c.courseName}
                          {c.city && <span style={{ color: '#94a3b8' }}> · {c.city}</span>}
                        </div>
                      ))}
                    </div>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {r.courses.map(c => (
                        <div key={c.courseId} style={{ fontSize: 13 }}>
                          <span style={pill(c.whoPaysCourse === 'integolf' ? '#dcfce7' : '#e0e7ff',
                                            c.whoPaysCourse === 'integolf' ? '#166534' : '#3730a3')}>
                            {c.whoPaysCourse}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

// ─── Rate picker modal (shared with provider detail) ────────────────────
function RatePickerModal({ row, onClose, onSaved }) {
  const [rates, setRates] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState('')
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setLoading(true); setError(null)
    const url = row.isVirtual
      ? `/admin/course-integrations/ttc-catalog-rates?courseCode=${encodeURIComponent(row.ttcCourseCode)}&teesheet=${encodeURIComponent(row.ttcTeesheet)}`
      : `/admin/course-integrations/${row.integration.id}/rates`
    tournamentApi.get(url)
      .then(r => setRates(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [row.isVirtual, row.ttcCourseCode, row.ttcTeesheet, row.integration?.id])

  async function applyRate(rate) {
    setSaving(true)
    try {
      if (row.isVirtual) {
        await tournamentApi.post('/admin/course-integrations/import-ttc', {
          ttcCourseCode: row.ttcCourseCode,
          ttcTeesheet:   row.ttcTeesheet,
          rateCode:      rate.rateCode,
          rateName:      rate.rateName,
        })
      } else {
        await tournamentApi.put(`/admin/course-integrations/${row.integration.id}/rate`, {
          rateCode: rate.rateCode,
          rateName: rate.rateName,
        })
      }
      onSaved()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
      setSaving(false)
    }
  }

  const filtered = useMemo(() => {
    if (!rates?.rates) return []
    let arr = rates.rates
    if (!showAll) arr = arr.filter(r => r.isLikelyPublic || r.isCurrent || r.isSuggested)
    if (filter) {
      const f = filter.toLowerCase()
      arr = arr.filter(r => r.rateName?.toLowerCase().includes(f) || r.rateCode?.toLowerCase().includes(f))
    }
    return arr
  }, [rates, filter, showAll])

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBody} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>
              {row.isVirtual ? 'Import & set rate' : 'Set default rate'}
            </h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{row.courseName}</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        {error && <div style={errorBoxStyle}>{error}</div>}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading rates…</div>
        ) : !rates?.rates?.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No rates returned.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
              <input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <Toggle
                checked={showAll}
                onChange={setShowAll}
                size="sm"
                label={`Show non-public (${rates.rates.length} total)`}
              />
            </div>
            <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {filtered.map(rate => (
                <div key={rate.rateCode} style={{
                  padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: rate.isCurrent ? '#f0fdfa' : 'transparent',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>
                      {rate.isLikelyPublic && <span style={{ color: '#f59e0b', marginRight: 4 }}>★</span>}
                      {rate.rateName || '(no name)'}
                      {rate.isCurrent && <span style={{ ...pill('#dcfce7', '#166534'), marginLeft: 8 }}>Current</span>}
                      {rate.isSuggested && !rate.isCurrent && <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 8 }}>Suggested</span>}
                    </div>
                    <code style={{ fontSize: 11, color: '#94a3b8' }}>{rate.rateCode}</code>
                  </div>
                  {!rate.isCurrent && (
                    <button onClick={() => applyRate(rate)} disabled={saving} style={smallBtn}>
                      {saving ? 'Saving…' : 'Use this'}
                    </button>
                  )}
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No matches. Try clearing the filter or checking "Show non-public".
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Building blocks ────────────────────────────────────────────────────
function Section({ title, right, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  )
}
function StatusBadge({ status }) {
  const map = {
    active:     { bg: '#dcfce7', fg: '#166534', label: 'Active' },
    awaiting:   { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting setup' },
    not_setup:  { bg: '#fee2e2', fg: '#991b1b', label: 'Not configured' },
    per_course: { bg: '#e0e7ff', fg: '#3730a3', label: 'Per-course credentials' },
    platform:   { bg: '#f1f5f9', fg: '#475569', label: 'Platform-owned' },
  }
  const m = map[status] || map.not_setup
  return <span style={{ ...pill(m.bg, m.fg), whiteSpace: 'nowrap' }}>{m.label}</span>
}
function Th({ children, style }) {
  return <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600, ...style }}>{children}</th>
}
function Td({ children, style }) {
  return <td style={{ padding: '12px 14px', fontSize: 14, color: '#0f172a', verticalAlign: 'top', ...style }}>{children}</td>
}
function pill(bg, fg) {
  return { display: 'inline-block', padding: '2px 8px', background: bg, color: fg, borderRadius: 12, fontSize: 11, fontWeight: 600 }
}

// ─── Styles ──────────────────────────────────────────────────────────────
const inputStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, background: '#fff', color: '#0f172a' }
const smallBtn = { padding: '4px 12px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer' }
const primaryBtnSmall = { padding: '4px 14px', background: '#00d19f', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const closeBtn = { background: 'transparent', border: 'none', fontSize: 24, color: '#64748b', cursor: 'pointer', lineHeight: 1, padding: 0 }
const errorBoxStyle = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 6, marginBottom: 16, fontSize: 14 }
const infoBoxStyle = { background: '#eff6ff', color: '#1e40af', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }
const linkStyle = { color: '#00d19f', textDecoration: 'none', fontSize: 14 }
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalBody = { background: '#fff', borderRadius: 10, padding: 24, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }
